import type { Router } from "vue-router";
import { postData } from "@/core/services/ApiService";
import { runtimeConfig } from "@/config/runtime";

/**
 * downloadBlob — trigger a browser file-save dialog for an in-memory Blob.
 *
 * Creates a temporary anchor element, clicks it, and cleans up.
 * The object URL is revoked after a short delay to give the browser time
 * to initiate the download (synchronous revoke can fail on Firefox).
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  _clickAndDownload(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/**
 * downloadDataUrl — trigger a browser file-save dialog for a data URL
 * (e.g. canvas.toDataURL() or a base64 image preview).
 */
export function downloadDataUrl(dataUrl: string, filename: string): void {
  _clickAndDownload(dataUrl, filename);
}

interface TicketResponse {
  url: string;
  expires_in: number;
}

/**
 * downloadViaTicket — kick off a *native* browser download for an
 * authenticated backend resource without putting the user's session token
 * in the URL.
 *
 * Flow:
 *   1. POST /api/download-tickets/\{resource\}  (authed via Authorization header)
 *      → backend returns `{ url, expires_in }` with a single-use 60s token
 *        and an API-relative URL.
 *   2. Anchor-click the resolved URL; the browser fetches it directly, so
 *      the file appears in the Downloads UI immediately with a native
 *      progress bar instead of a JS-side blob spinner.
 *
 * Resolves once the navigation is initiated; the actual transfer is owned
 * by the browser and continues independently of this promise.
 */
export async function downloadViaTicket(
  router: Router,
  resource: string,
  filename: string,
): Promise<void> {
  const response = await postData(router, `download-tickets/${resource}`, "{}");
  if (!response || !response.ok) {
    throw new Error(`Failed to mint download ticket for "${resource}"`);
  }
  const { url } = (await response.json()) as TicketResponse;
  _clickAndDownload(`${runtimeConfig.apiUrl}/${url}`, filename);
}

/** Shared anchor-click logic. Appending to body is required for Firefox. */
function _clickAndDownload(href: string, filename: string): void {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
